// Admin API client (§8). The dashboard is same-origin with the admin worker behind Cloudflare
// Access (the Access JWT cookie rides along automatically), so we just fetch `/api/v1/*`.
// Optimistic concurrency (§11): mutating PUT/PATCH carry If-Match; 412 surfaces a conflict.

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isConflict() {
    return this.status === 412 || this.status === 409;
  }
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  ifMatch?: number | string;
  idempotencyKey?: string;
  query?: Record<string, string | number | undefined | null>;
}

function buildUrl(path: string, query?: RequestOpts["query"]): string {
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== "") url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (opts.ifMatch != null) headers["If-Match"] = String(opts.ifMatch);
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "same-origin",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const err = (data as { error?: { message?: string; code?: string; details?: unknown } })?.error;
    throw new ApiError(res.status, err?.message ?? res.statusText, err?.code, err?.details);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---------------------------------------------------------------------------
// Domain types (mirror the admin API / D1 schema — kept local to avoid pulling the
// server-only @cadence/shared package, which depends on node/cloudflare types).
// ---------------------------------------------------------------------------

export interface Page<T> {
  data: T[];
  nextCursor: string | null;
}

export interface Service {
  id: string;
  name: string;
  baseUrl: string;
  signingKeys: SigningKey[] | null;
  signingMode: "required" | "none";
  defaultTimeoutMs: number;
  maintenanceWindows: MaintenanceWindow[] | null;
  maxRps: number | null;
  maxParallelism: number | null;
  maxTasks: number | null;
  maxPayloadBytes: number;
  retainDays: number;
  enabled: boolean;
  version: number;
  createdAt: number | string;
  updatedAt: number | string;
}

export interface SigningKey {
  kid: string;
  secretName: string;
  active: boolean;
  createdAt: number;
}

export interface MaintenanceWindow {
  tz: string;
  days: number[];
  start: string;
  end: string;
}

export interface GroupMember {
  path: string;
  method: string;
}

export interface TargetGroup {
  id: string;
  serviceId: string;
  members: GroupMember[];
  version: number;
  createdAt: number | string;
  updatedAt: number | string;
}

export type ScheduleKind = "cron" | "interval" | "one_shot";
export type ScheduleEngine = "tick" | "do_alarm";
export type TargetType = "http" | "workflow" | "group";

export interface Task {
  id: string;
  serviceId: string;
  targetType: TargetType;
  path: string | null;
  method: string;
  workflowBinding: string | null;
  targetGroupId: string | null;
  scheduleKind: ScheduleKind;
  scheduleEngine: ScheduleEngine;
  cron: string | null;
  intervalSeconds: number | null;
  notBefore: number | string | null;
  timezone: string;
  payload: unknown;
  payloadSecretRefs: string[] | null;
  callbackUrl: string | null;
  failureCallbackUrl: string | null;
  maxRps: number | null;
  maxParallelism: number | null;
  dedupWindowSeconds: number | null;
  enabled: boolean;
  paused: boolean;
  misfirePolicy: string;
  overlapPolicy: string;
  maxAttempts: number;
  backoffBaseSeconds: number;
  backoffFactor: number;
  maxBackoffSeconds: number;
  timeoutMs: number | null;
  retainDays: number | null;
  tags: string[] | null;
  nextRunAt: number | string | null;
  version: number;
  deletedAt: number | string | null;
  createdAt: number | string;
  updatedAt: number | string;
}

export type RunStatus =
  | "pending"
  | "running"
  | "deferred"
  | "succeeded"
  | "failed"
  | "dead"
  | "skipped";

export interface Run {
  id: string;
  taskId: string;
  scheduledSlot: number | string;
  attempt: number;
  status: RunStatus;
  idempotencyKey: string | null;
  dedupId: string | null;
  contentHash: string | null;
  correlationId: string | null;
  traceparent: string | null;
  groupMemberIndex: number | null;
  leaseExpiresAt: number | string | null;
  trigger: "schedule" | "manual" | "replay" | "one_shot";
  startedAt: number | string | null;
  finishedAt: number | string | null;
  durationMs: number | null;
  responseStatus: number | null;
  result: unknown;
  error: string | null;
  createdAt: number | string;
}

export interface RunAttempt {
  id: string;
  runId: string;
  attempt: number;
  status: string;
  responseStatus: number | null;
  error: string | null;
  startedAt: number | string | null;
  finishedAt: number | string | null;
  durationMs: number | null;
  callbackStatus: number | null;
  createdAt: number | string;
}

export interface RunDetail extends Run {
  attempts: RunAttempt[];
}

// Fan-out aggregation (§6.9). A group task fires one run per member; the admin derives a per-slot
// rollup of the member outcomes.
export type FanoutSlotStatus = "succeeded" | "failed" | "partial" | "running" | "skipped";

export interface FanoutSlot {
  slot: number; // canonical scheduled slot (epoch ms)
  members: number;
  succeeded: number;
  failed: number;
  dead: number;
  skipped: number;
  pending: number;
  status: FanoutSlotStatus;
}

export interface FanoutSlotsPage {
  taskId: string;
  data: FanoutSlot[];
  nextCursor: string | null;
}

export interface FanoutMembers {
  taskId: string;
  slot: number;
  members: Run[];
}

export interface Overview {
  generatedAt: number;
  windowMs: number;
  dueSoon: number;
  last24h: {
    succeeded: number;
    failed: number;
    dead: number;
    skipped: number;
    total: number;
    successRate: number | null;
  };
  dlqBacklog: number;
  inFlight: number;
  schedulingLagMs: number;
  heartbeats: Array<{
    component: string;
    lastBeatAt: number;
    ageMs: number;
    stale: boolean;
    detail: unknown;
  }>;
  tickAlive: boolean;
}

// ---------------------------------------------------------------------------
// Typed endpoint helpers
// ---------------------------------------------------------------------------

export const Api = {
  overview: () => api<Overview>("/overview"),

  listServices: () => api<{ data: Service[]; nextCursor: string | null }>("/services"),
  getService: (id: string) => api<Service>(`/services/${id}`),
  upsertService: (body: unknown) => api<Service>("/services", { method: "POST", body }),
  updateService: (id: string, body: unknown, ifMatch: number) =>
    api<Service>(`/services/${id}`, { method: "PATCH", body, ifMatch }),
  deleteService: (id: string, ifMatch: number) =>
    api<void>(`/services/${id}`, { method: "DELETE", ifMatch }),

  listGroups: () => api<{ data: TargetGroup[]; nextCursor: string | null }>("/groups"),
  upsertGroup: (body: unknown) => api<TargetGroup>("/groups", { method: "POST", body }),

  listTasks: (query?: Record<string, string | undefined>) =>
    api<{ data: Task[]; nextCursor: string | null }>("/tasks", { query }),
  getTask: (id: string) => api<Task>(`/tasks/${id}`),
  upsertTask: (body: unknown) => api<Task>("/tasks", { method: "POST", body }),
  updateTask: (id: string, body: unknown, ifMatch: number) =>
    api<Task>(`/tasks/${id}`, { method: "PATCH", body, ifMatch }),
  deleteTask: (id: string, ifMatch: number) =>
    api<void>(`/tasks/${id}`, { method: "DELETE", ifMatch }),
  restoreTask: (id: string) => api<Task>(`/tasks/${id}/restore`, { method: "POST" }),
  pauseTask: (id: string, ifMatch: number) =>
    api<Task>(`/tasks/${id}/pause`, { method: "POST", ifMatch }),
  resumeTask: (id: string, ifMatch: number) =>
    api<Task>(`/tasks/${id}/resume`, { method: "POST", ifMatch }),
  runNow: (id: string) => api<Run>(`/tasks/${id}/run`, { method: "POST" }),

  listRuns: (query?: Record<string, string | undefined>) =>
    api<Page<Run>>("/runs", { query }),
  getRun: (id: string) => api<RunDetail>(`/runs/${id}`),
  replayRun: (id: string) => api<Run>(`/runs/${id}/replay`, { method: "POST" }),

  fanoutSlots: (taskId: string, query?: Record<string, string | undefined>) =>
    api<FanoutSlotsPage>(`/fanout/${taskId}`, { query }),
  fanoutMembers: (taskId: string, slot: number) =>
    api<FanoutMembers>(`/fanout/${taskId}/${slot}`),

  // Secrets Store auto-provisioning (Option B). `secretsConfig` tells the UI whether to show the
  // "Save to Secrets Store" button; `provisionSecret` writes one per-service secret (the raw value
  // is sent once and never persisted by Cadence).
  secretsConfig: () => api<{ configured: boolean; storeId: string | null }>("/secrets/config"),
  provisionSecret: (body: { serviceId: string; kid: string; secretName: string; secret: string }) =>
    api<{ ok: boolean; action: "created" | "updated"; secretName: string }>("/secrets/provision", {
      method: "POST",
      body,
    }),

  listDlq: (query?: Record<string, string | undefined>) => api<Page<Run>>("/dlq", { query }),
  requeueDlq: (body: { ids?: string[]; filter?: Record<string, unknown> }) =>
    api<{ requeued: number; ids: string[] }>("/dlq/requeue", { method: "POST", body }),
  deleteDlq: (body: { ids?: string[]; filter?: Record<string, unknown> }) =>
    api<{ deleted: number; ids: string[] }>("/dlq/delete", { method: "POST", body }),
};